import type { PluginToolbarButtonConfig } from 'ricos-types';
import { TIPTAP_ACTION_BUTTON_TYPE, TIPTAP_LINK_BUTTON_TYPE } from 'wix-rich-content-common';
import { PLUGIN_TOOLBAR_BUTTON_ID } from 'wix-rich-content-editor-common';
import { LinkIcon } from 'wix-rich-content-toolbars-ui';
import {
  getNodeAlignmentResolver,
  getNodeInSelectionResolver,
  getNodeLinkDataResolver,
  getNodeSizeResolver,
  getNodeTextWrapResolver,
  isNodeContainsLinkOrAnchorResolver,
} from 'wix-rich-content-toolbars-v3';

type PluginButtonId = (typeof PLUGIN_TOOLBAR_BUTTON_ID)[keyof typeof PLUGIN_TOOLBAR_BUTTON_ID];

type IPluginToolbarButtonsConfig = (props: {
  isMobile: boolean;
}) => Record<PluginButtonId, PluginToolbarButtonConfig>;

export const toolbarButtonsConfig: IPluginToolbarButtonsConfig = ({ isMobile }) => ({
  delete: {
    id: PLUGIN_TOOLBAR_BUTTON_ID.DELETE,
    type: 'toggle',
    attributes: {
      selectedNode: getNodeInSelectionResolver,
    },
    presentation: {
      dataHook: 'blockButton_delete',
    },
    commands: {
      delete: ({ editorCommands, attributes: { selectedNode } }) => {
        editorCommands.chain().focus().deleteNode(selectedNode.attrs.id).run();
      },
    },
  },
  alignment: {
    id: PLUGIN_TOOLBAR_BUTTON_ID.ALIGNMENT,
    type: 'modal',
    attributes: {
      nodeAlignment: getNodeAlignmentResolver,
      nodeTextWrap: getNodeTextWrapResolver,
      visible: {
        id: 'IS_ALIGNMENT_BUTTON_VISIBLE',
        resolve: () => !isMobile,
      },
    },
    presentation: {
      dataHook: 'nodeAlignmentButton',
    },
    commands: {
      setAlignment: ({ editorCommands, value, attributes: { selectedNode } }) => {
        editorCommands
          .chain()
          .focus()
          .setNodeAlignment(value)
          .command(({ commands }) => {
            const isCustomWidthSpecified = selectedNode?.attrs.containerData?.width?.custom;
            const excludedNodeTypes = [TIPTAP_LINK_BUTTON_TYPE, TIPTAP_ACTION_BUTTON_TYPE];

            if (!isCustomWidthSpecified && !excludedNodeTypes.includes(selectedNode.type.name)) {
              commands.setNodeSize('SMALL');
            }

            return true;
          })
          .run();
      },
      toggleTextWrap: ({ editorCommands }) => {
        editorCommands.chain().focus().toggleNodeTextWrap().run();
      },
    },
  },
  size: {
    id: PLUGIN_TOOLBAR_BUTTON_ID.SIZE,
    type: 'modal',
    attributes: {
      nodeSize: getNodeSizeResolver,
      visible: {
        id: 'IS_SIZE_BUTTON_VISIBLE',
        resolve: () => !isMobile,
      },
    },
    presentation: {
      dataHook: 'nodeSizeButton',
    },
    commands: {
      setSize: ({ editorCommands, value }) => {
        editorCommands.chain().focus().setNodeSize(value).setNodeAlignment('CENTER').run();
      },
    },
  },
  settings: {
    id: PLUGIN_TOOLBAR_BUTTON_ID.SETTINGS,
    type: 'modal',
    presentation: {
      dataHook: 'baseToolbarButton_settings',
    },
    attributes: {
      selectedNode: getNodeInSelectionResolver,
    },
    commands: {},
  },
  replace: {
    id: PLUGIN_TOOLBAR_BUTTON_ID.REPLACE,
    type: 'toggle',
    presentation: {
      dataHook: 'baseToolbarButton_replace',
    },
    attributes: {
      selectedNode: getNodeInSelectionResolver,
    },
    commands: {},
  },
  link: {
    id: PLUGIN_TOOLBAR_BUTTON_ID.LINK,
    type: 'modal',
    presentation: {
      dataHook: 'LinkButton',
      tooltip: 'TextLinkButton_Tooltip',
      icon: LinkIcon,
    },
    attributes: {
      selectedNode: getNodeInSelectionResolver,
      active: isNodeContainsLinkOrAnchorResolver,
      linkData: getNodeLinkDataResolver,
    },
    commands: {
      insertLink: ({ editorCommands, attributes: { selectedNode }, link }) => {
        editorCommands.chain().focus().updateAttributes(selectedNode.type.name, { link }).run();
      },
      removeLink: ({ editorCommands, attributes: { selectedNode } }) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { link, ...attrs } = selectedNode.attrs;
        editorCommands.chain().focus().setNodeAttrsById(attrs.id, attrs).run();
      },
    },
  },
  separator: {
    id: PLUGIN_TOOLBAR_BUTTON_ID.SEPARATOR,
    type: 'separator',
    presentation: {},
    attributes: {},
    commands: {},
  },
});
